// src/pages/fourd/Project4DUploadPage.jsx
import React, { useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { 
  FolderPlus, Upload, Trash2, FileText, 
  Eye, X, CornerDownRight, Download
} from "lucide-react";
import { useDropzone } from "react-dropzone";

import BimFileTree from "../../components/common/fourd/BimFileTree";
import ThreeViewer from "../../components/viewer/viewer3d/ThreeViewer";
import {
  createBimFolder,
  deleteBimFolder,
  uploadBimFile,
  deleteBimFile,
} from "../../api/bim";

/**
 * 모달 컴포넌트 (공통)
 */
const Modal = ({ isOpen, onClose, title, children, size = "md" }) => {
  if (!isOpen) return null;
  const sizeClasses = {
      sm: "max-w-sm",
      md: "max-w-lg",
      lg: "max-w-2xl",
      xl: "max-w-6xl h-[80vh]",
      full: "max-w-full h-full m-4"
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className={`bg-white rounded-xl shadow-2xl w-full ${sizeClasses[size]} overflow-hidden flex flex-col max-h-[90vh]`}>
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="text-lg font-bold text-gray-800">{title}</h3>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-100 transition">
            <X size={20} className="text-gray-500" />
          </button>
        </div>
        <div className="flex-1 overflow-auto p-6">{children}</div>
      </div>
    </div>
  );
};

const formatBytes = (bytes, decimals = 2) => {
    if (!+bytes) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

const Project4DUploadPage = () => {

  const { id: projectId } = useParams();

  // State
  const [selectedFolder, setSelectedFolder] = useState(null); // 현재 선택된 폴더 (없으면 루트? 또는 null)
  const [folderFiles, setFolderFiles] = useState([]); // 현재 폴더의 파일 목록
  const [treeRefreshKey, setTreeRefreshKey] = useState(0);

  // Modal State
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isNewFolderModalOpen, setIsNewFolderModalOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null); // { gltf_file, name }

  // Upload State
  const [uploadFiles, setUploadFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  
  // New Folder State
  const [newFolderName, setNewFolderName] = useState("");
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);

  // ---- Handlers ----

  const handleFolderSelect = (folderNode) => {
    setSelectedFolder(folderNode);
    // folderNode.files 에 파일 목록이 있음 (API 구조에 맟춰)
    setFolderFiles(folderNode.files || []);
  };

  const refreshTree = () => {
    setTreeRefreshKey((prev) => prev + 1);
  };

  // 폴더 삭제
  const handleDeleteFolder = async () => {
    if (!selectedFolder) return;
    if (!window.confirm(`'${selectedFolder.name}' 폴더와 내부 파일이 모두 삭제됩니다.\n계속하시겠습니까?`)) return;

    try {
      await deleteBimFolder(selectedFolder.id);
      refreshTree();
      setSelectedFolder(null);
      setFolderFiles([]);
      alert("폴더가 삭제되었습니다.");
    } catch (e) {
      console.error(e);
      alert("삭제 실패: " + (e.response?.data?.error || e.message));
    }
  };

  // 코드 간소화: 폴더 생성
  const handleCreateFolder = async () => {
      if (!newFolderName.trim()) return alert("폴더명을 입력하세요.");
      try {
          setIsCreatingFolder(true);
          await createBimFolder({
              project: projectId,
              parent: selectedFolder ? selectedFolder.id : null, 
              name: newFolderName.trim()
          });
          refreshTree();
          setIsNewFolderModalOpen(false);
          setNewFolderName("");
          alert("폴더가 생성되었습니다.");
      } catch(e) {
          alert("생성 실패: " + e.message);
      } finally {
          setIsCreatingFolder(false);
      }
  };

  // 파일 삭제
  const handleDeleteFile = async (fileId) => {
      if(!window.confirm("정말 이 파일을 삭제하시겠습니까?")) return;
      try {
          await deleteBimFile(fileId);
          // 파일 목록만 갱신하거나 트리 전체 갱신
          refreshTree(); 
          // 현재 파일 목록에서 제거 (Optimistic UI)
          setFolderFiles(prev => prev.filter(f => f.id !== fileId));
          if (previewFile?.id === fileId) setPreviewFile(null);
      } catch(e) {
          alert("삭제 실패: " + e.message);
      }
  };

  // 파일 업로드 Dropzone
  const onDrop = useCallback((acceptedFiles) => {
      // 기존 파일 목록에 추가
      setUploadFiles(prev => [...prev, ...acceptedFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, multiple: true });

  const handleExecuteUpload = async () => {
      if (uploadFiles.length === 0) return;
      
      const ifc = uploadFiles.find(f => f.name.toLowerCase().endsWith('.ifc'));
      const g = uploadFiles.find(f => f.name.toLowerCase().endsWith('.gltf'));
      const b = uploadFiles.find(f => f.name.toLowerCase().endsWith('.bin'));
      
      // Validation: Either (IFC) OR (GLTF + BIN)
      if (!ifc && (!g || !b)) {
          alert("업로드하려면 .ifc 파일 단독, 또는 (.gltf + .bin) 파일 쌍이 필요합니다.");
          return;
      }

      try {
          setIsUploading(true);
          const formData = new FormData();
          formData.append("project", projectId);
          if (selectedFolder) formData.append("folder", selectedFolder.id);

          if (ifc) {
              formData.append("name", ifc.name);
              formData.append("gltf_file", ifc); // Reusing gltf_file field for IFC
              // formData.append("bin_file", null); // Not needed
          } else {
              formData.append("name", g.name);
              formData.append("gltf_file", g);
              formData.append("bin_file", b);
          }

          await uploadBimFile(formData);
          
          alert("업로드 완료!");
          setUploadFiles([]);
          setIsUploadModalOpen(false);
          refreshTree();
      } catch(e) {
          console.error(e);
          alert("업로드 실패: " + (e.response?.data?.error || e.message));
      } finally {
          setIsUploading(false);
      }
  };


  return (
    <div className="flex flex-col h-full bg-gray-50 min-h-[calc(100vh-64px)]">
      {/* 헤더 */}
      <div className="px-6 py-4 border-b bg-white flex items-center justify-between shrink-0 shadow-sm">
        <div>
           <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            🏗️ 4D 파일 관리 (업로드)
           </h2>
           <p className="text-xs text-gray-500 mt-1">
             프로젝트: <span className="text-blue-600 font-medium">{projectId}</span> | 폴더를 생성하고 glTF/BIN 파일을 업로드하세요.
           </p>
        </div>
      </div>

      {/* 메인 콘텐츠 Split View */}
      <div className="flex-1 flex overflow-hidden p-4 gap-4">
        
        {/* 좌측: 트리 영역 */}
        <div className="w-[300px] shrink-0 h-full flex flex-col">
            <BimFileTree 
                refreshKey={treeRefreshKey}
                onFolderSelect={handleFolderSelect}
                onPick={(file) => setPreviewFile(file)}
            />
        </div>

        {/* 우측: 파일 리스트 & 작업 영역 */}
        <div className="flex-1 bg-white border rounded-xl shadow-sm flex flex-col overflow-hidden">
            
            {/* 툴바 */}
            <div className="p-4 border-b flex items-center justify-between bg-gray-50/50">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                    <span className="bg-gray-200 px-2 py-1 rounded text-xs font-semibold text-gray-700">현재 위치</span>
                    <CornerDownRight size={14} className="text-gray-400" />
                    {selectedFolder ? (
                        <span className="font-bold text-blue-700">{selectedFolder.name}</span>
                    ) : (
                        <span className="text-gray-400 italic">(폴더를 선택하세요)</span>
                    )}
                </div>

                <div className="flex gap-2">
                     <button 
                        onClick={() => setIsNewFolderModalOpen(true)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                     >
                        <FolderPlus size={14} /> 새 폴더
                     </button>
                     {selectedFolder && (
                         <button 
                            onClick={handleDeleteFolder}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 transition"
                         >
                            <Trash2 size={14} /> 폴더 삭제
                         </button>
                     )}
                     <div className="w-px h-6 bg-gray-300 mx-1"></div>
                     <button
                        onClick={() => {
                             if(!selectedFolder) return alert("파일을 업로드할 폴더를 먼저 선택하세요.");
                             setIsUploadModalOpen(true);
                        }}
                        className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-bold text-white bg-blue-600 rounded-lg hover:bg-blue-700 shadow-sm transition"
                     >
                        <Upload size={14} /> 파일 업로드
                     </button>
                </div>
            </div>

            {/* 파일 테이블 */}
            <div className="flex-1 overflow-auto">
                {selectedFolder ? (
                    folderFiles.length > 0 ? (
                        <table className="w-full text-left border-collapse">
                            <thead className="bg-gray-50 sticky top-0 z-10 text-xs uppercase text-gray-500 font-semibold">
                                <tr>
                                    <th className="px-6 py-3 border-b">파일 이름</th>
                                    <th className="px-6 py-3 border-b w-32">크기</th>
                                    <th className="px-6 py-3 border-b w-40">업로드 일시</th>
                                    <th className="px-6 py-3 border-b w-24 text-center">작업</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 text-sm">
                                {folderFiles.map((file) => (
                                    <tr key={file.id} className="hover:bg-blue-50/50 transition group">
                                        <td className="px-6 py-3 font-medium text-gray-800 flex items-center gap-2">
                                            <div className="p-1.5 bg-blue-100 rounded text-blue-600">
                                                <FileText size={16} />
                                            </div>
                                            <span onClick={() => setPreviewFile(file)} className="cursor-pointer hover:underline decoration-blue-400 underline-offset-2">
                                                {file.name}
                                            </span>
                                        </td>
                                        <td className="px-6 py-3 text-gray-500 font-mono text-xs">
                                            {formatBytes(file.size || file.file_size || 0)}
                                        </td>
                                        <td className="px-6 py-3 text-gray-500 text-xs">
                                            {new Date(file.uploaded_at || Date.now()).toLocaleDateString()}
                                        </td>
                                        <td className="px-6 py-3 text-center">
                                            <div className="flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <button onClick={() => {
                                                    const path = file.gltf_file || file.file;
                                                    // If path is relative (/media/...), prepend backend origin
                                                    const url = path.startsWith("http") ? path : `http://localhost:8000${path}`;
                                                    window.open(url, '_blank');
                                                }} title="다운로드" className="p-1.5 text-slate-500 hover:text-green-600 hover:bg-green-100 rounded">
                                                    <Download size={14} />
                                                </button>
                                                <button onClick={() => setPreviewFile(file)} title="미리보기" className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-100 rounded">
                                                    <Eye size={14} />
                                                </button>
                                                <button onClick={() => handleDeleteFile(file.id)} title="삭제" className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-100 rounded">
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                         <div className="h-full flex flex-col items-center justify-center text-gray-400">
                            <FolderPlus size={48} className="mb-4 text-gray-200" />
                            <p className="text-sm">이 폴더는 비어있습니다.</p>
                            <button onClick={() => setIsUploadModalOpen(true)} className="mt-4 text-xs text-blue-600 hover:underline">
                                파일 업로드하기
                            </button>
                         </div>
                    )
                ) : (
                    <div className="h-full flex flex-col items-center justify-center text-gray-400">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4 text-gray-300">
                            <CornerDownRight size={32} />
                        </div>
                        <p className="font-medium text-gray-600">폴더를 선택해주세요</p>
                        <p className="text-xs text-gray-400 mt-1">좌측 트리에서 폴더를 선택하면 파일 목록이 표시됩니다.</p>
                    </div>
                )}
            </div>
        </div>
      </div>

        {/* --- Modals --- */}
        
        {/* 1. 파일 업로드 모달 */}
        <Modal isOpen={isUploadModalOpen} onClose={() => setIsUploadModalOpen(false)} title="파일 업로드">
             <div className="space-y-6">
                <div 
                    {...getRootProps()} 
                    className={`border-2 border-dashed rounded-xl p-8 flex flex-col items-center justify-center text-center transition-colors cursor-pointer
                        ${isDragActive ? "border-blue-500 bg-blue-50" : "border-gray-300 hover:border-blue-400"}`}
                >
                    <input {...getInputProps()} />
                    <Upload size={32} className="text-gray-400 mb-3" />
                    <p className="text-sm font-medium text-gray-700">
                        여기를 클릭하거나 파일을 드래그하여 놓으세요.
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                        .ifc 파일을 업로드하거나, (.gltf + .bin) 파일을 함께 업로드하세요.
                    </p>
                </div>
                
                {uploadFiles.length > 0 && (
                    <div className="bg-gray-50 rounded-lg p-4 border block">
                        <p className="text-xs font-bold text-gray-500 uppercase mb-2">선택된 파일 ({uploadFiles.length})</p>
                        <ul className="space-y-2 max-h-32 overflow-y-auto">
                            {uploadFiles.map((f, i) => (
                                <li key={i} className="flex items-center justify-between text-sm bg-white p-2 rounded border">
                                    <span className="truncate max-w-[80%]">{f.name}</span>
                                    <button onClick={() => setUploadFiles(prev => prev.filter((_, idx) => idx !== i))} className="text-gray-400 hover:text-red-500">
                                        <X size={14} />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                <div className="flex justify-end pt-4">
                    <button 
                         onClick={handleExecuteUpload} 
                         disabled={isUploading || uploadFiles.length === 0}
                         className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition"
                    >
                        {isUploading ? "업로드 중..." : "업로드 완료"}
                    </button>
                </div>
             </div>
        </Modal>

        {/* 2. 새 폴더 모달 */}
        <Modal isOpen={isNewFolderModalOpen} onClose={() => setIsNewFolderModalOpen(false)} title="새 폴더 생성" size="sm"> 
            <div className="space-y-4">
                <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">상위 폴더</label>
                    <div className="p-2 bg-gray-100 rounded text-sm text-gray-700 font-medium truncate">
                        {selectedFolder ? selectedFolder.name : "(최상위 루트)"}
                    </div>
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">폴더명</label>
                    <input 
                        value={newFolderName}
                        onChange={(e) => setNewFolderName(e.target.value)}
                        placeholder="예: 지상 1층 구조물"
                        className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-100 outline-none border-gray-300"
                        autoFocus
                    />
                </div>
                <div className="flex justify-end pt-2">
                    <button 
                        onClick={handleCreateFolder}
                        disabled={isCreatingFolder}
                        className="px-4 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-900 text-sm font-medium disabled:opacity-50"
                    >
                        {isCreatingFolder ? "생성 중..." : "생성하기"}
                    </button>
                </div>
            </div>
        </Modal>

        {/* 3. 미리보기 모달 */}
        <Modal isOpen={!!previewFile} onClose={() => setPreviewFile(null)} title={`미리보기: ${previewFile?.name || ''}`} size="xl">
            <div className="w-full h-full bg-gray-100 rounded-xl overflow-hidden relative">
                {previewFile && (
                    <ThreeViewer gltfUrl={previewFile.gltf_file || previewFile.file} />
                )}
            </div>
        </Modal>
    </div>
  );
};

export default Project4DUploadPage;
